import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { subscribeToAudience } from "@/lib/audience.functions";
import { ALL_LESSONS } from "@/data/lessons";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/")({
  head: () => {
    const title = "WHO KNEW - Field guides for people who've been confidently nodding along";
    const description =
      "Plain-English courses on the topics most business people quietly wish they understood. First one out: reading a set of UK company accounts, in three hours, without the jargon.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "https://whoknew.guide/" },
      ],
    };
  },
  component: Home,
});

const MONO = '"JetBrains Mono", monospace';

function Home() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const subscribe = useServerFn(subscribeToAudience);
  const [hasSession, setHasSession] = useState(false);
  const [signedOutBanner, setSignedOutBanner] = useState(false);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setHasSession(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setHasSession(!!session);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("signed_out") === "1") {
        setSignedOutBanner(true);
        url.searchParams.delete("signed_out");
        window.history.replaceState({}, "", url.pathname + url.search);
        const t = setTimeout(() => setSignedOutBanner(false), 5000);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  const onCourseClick = (e: React.MouseEvent) => {
    if (!hasSession) return; // let Link handle navigation to marketing page
    e.preventDefault();
    try {
      const raw = window.localStorage.getItem("wk-completed-lessons");
      const arr = raw ? JSON.parse(raw) : [];
      const done = new Set(Array.isArray(arr) ? arr.filter((x: any) => typeof x === "string") : []);
      const next = ALL_LESSONS.find((l) => !done.has(l.id)) ?? ALL_LESSONS[0];
      navigate({
        to: "/courses/business-and-accountancy/basic-business-accountancy/lessons/$lessonId",
        params: { lessonId: next.id },
      });
    } catch {
      navigate({
        to: "/courses/business-and-accountancy/basic-business-accountancy/lessons/$lessonId",
        params: { lessonId: "1.1" },
      });
    }
  };

  const onSignBackIn = () => {
    let last = "";
    try {
      last = window.localStorage.getItem("last_signed_in_email") ?? "";
    } catch {}
    setSignedOutBanner(false);
    navigate({ to: "/signin", search: last ? { email: last } : {} });
  };


  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      const sourceUrl =
        typeof window !== "undefined" ? window.location.href : "";
      const result = await subscribe({
        data: { email: email.trim(), sourceUrl, website },
      });
      if (result.ok) {
        setSuccessMsg(
          "On the list. You'll hear from us when there's actually something to say.",
        );
        setEmail("");
      } else if (result.error === "rate_limited") {
        const { formatRateLimitMessage } = await import("@/lib/rate-limit-message");
        setErrorMsg(formatRateLimitMessage(result.retryAfterSeconds));
      } else if (result.error === "validation") {
        setErrorMsg("That's not an email. Try again?");
      } else {
        setErrorMsg(
          "Our end, not yours. Try again or email hello@whoknew.guide.",
        );
      }
    } catch (err) {
      // Zod validation throws via inputValidator → surfaces as thrown error
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("validation")) {
        setErrorMsg("That's not an email. Try again?");
      } else {
        setErrorMsg(
          "Our end, not yours. Try again or email hello@whoknew.guide.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />

      {signedOutBanner && (
        <div
          className="flex items-center justify-center gap-4 px-4 py-3"
          style={{ backgroundColor: "#E8E1D3" }}
        >
          <p
            className="italic text-ink"
            style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "14px" }}
          >
            Signed out.{" "}
            <button
              type="button"
              onClick={onSignBackIn}
              className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2 bg-transparent cursor-pointer p-0"
              style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "14px" }}
            >
              Sign back in
            </button>
          </p>
          <button
            type="button"
            onClick={() => setSignedOutBanner(false)}
            aria-label="Dismiss"
            className="text-ink/70 hover:text-ink bg-transparent cursor-pointer p-0"
            style={{ fontSize: "16px", lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      )}

      <main className="flex flex-1 items-center justify-center px-6 pt-[80px] md:pt-0">
        <div className="w-full max-w-[720px]">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
            style={{ fontFamily: MONO }}
          >
            A FIELD GUIDE
          </p>

          <h1 className="mt-6 text-[28px] md:text-[44px] font-bold leading-[1.15] text-ink">
            Field guides for people who’ve spent a long time saying ‘yep, makes sense’ when it absolutely did not make sense.
          </h1>

          <hr className="mt-8 w-[60px] border-0 h-[2px] bg-burgundy" />

          <p className="mt-8 text-[18px] leading-[1.6] text-ink">Our first course is out.</p>

          <p className="mt-6">
            <Link
              to="/courses/business-and-accountancy/basic-business-accountancy"
              onClick={onCourseClick}
              className="link-hit text-[22px] italic leading-[1.4] text-burgundy underline decoration-burgundy/50 decoration-1 underline-offset-4 transition-all hover:decoration-2 hover:decoration-burgundy"
            >
              The Basic Business Accountancy Course for People Who Will Not, Under Any
              Circumstances, Take an Accountancy Course →
            </Link>
          </p>

          <p
            className="mt-12 text-[10px] font-bold uppercase tracking-[0.05em] text-muted-foreground"
            style={{ fontFamily: MONO }}
          >
            MORE COMING SOON · GET ALERTED:
          </p>

          <form onSubmit={onSubmit} className="mt-4 flex w-full max-w-[480px] gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-paper-dark px-4 py-3 text-[14px] text-ink placeholder:text-muted-foreground focus:outline-none"
              style={{ fontFamily: MONO }}
            />
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              aria-hidden="true"
              tabIndex={-1}
              autoComplete="off"
              style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
            />
            <button
              type="submit"
              disabled={submitting}
              className="interactive-lift bg-burgundy px-5 py-3 text-[14px] font-bold uppercase tracking-[0.05em] text-paper transition-colors hover:bg-burgundy-2 disabled:opacity-70"
              style={{ fontFamily: MONO }}
            >
              {submitting ? "..." : "NOTIFY ME"}
            </button>
          </form>
          {successMsg && (
            <p
              className="mt-3 italic text-[14px] text-muted-foreground"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              {successMsg}
            </p>
          )}
          {errorMsg && (
            <p
              className="mt-3 italic text-[14px] text-burgundy"
              style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
            >
              {errorMsg}
            </p>
          )}

        </div>
      </main>

      <footer className="flex w-full flex-col items-center">
        <hr className="border-0 h-px bg-burgundy w-full max-w-[720px]" />
        <p
          className="mt-6 font-bold uppercase tracking-[0.1em] text-burgundy text-[16px] text-center"
          style={{ fontFamily: MONO }}
        >
          WHO KNEW
        </p>
        <p
          className="mt-3 font-bold uppercase tracking-[0.05em] text-[10px] text-muted-foreground text-center"
          style={{ fontFamily: MONO }}
        >
          © 2026 WHO KNEW. ALL RIGHTS RESERVED.
        </p>
        <div
          className="mt-4 mb-6 font-bold uppercase tracking-[0.05em] text-[10px] text-burgundy text-center"
          style={{ fontFamily: MONO }}
        >
          <Link to="/privacy" className="link-hit hover:underline">Privacy</Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="link-hit hover:underline">Terms</Link>
          <span className="mx-2">·</span>
          <Link to="/refunds" className="link-hit hover:underline">Refunds</Link>
          <span className="mx-2">·</span>
          <Link to="/teams" className="link-hit hover:underline">For teams</Link>
        </div>

      </footer>
    </div>
  );
}
